import { useState, useEffect, useCallback } from 'react';
import { MeetingService } from '../services/meetingService';

interface AttendanceRecord {
  id: string;
  member_id: string;
  meeting_instance_id: string;
  status: 'Present' | 'Absent';
  members: {
    id: string;
    name: string;
    phone: string;
  };
}

export function useAttendance(meetingInstanceId?: string) {
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadAttendance = useCallback(async () => {
    if (!meetingInstanceId) return;

    setLoading(true);
    setError('');

    try {
      const { data, error } = await MeetingService.getAttendanceForMeeting(meetingInstanceId);
      if (data) {
        setAttendance(data);
      } else if (error) {
        setError(error.message);
      }
    } catch (err) {
      setError('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  }, [meetingInstanceId]);

  useEffect(() => {
    loadAttendance();
  }, [loadAttendance]);

  const updateAttendance = async (memberId: string, status: 'Present' | 'Absent') => {
    if (!meetingInstanceId) return;

    try {
      const { data, error } = await MeetingService.updateAttendance(meetingInstanceId, memberId, status);
      if (data) {
        // Also ensure the meeting is marked as Completed when attendance exists
        await MeetingService.updateInstance(meetingInstanceId, { status: 'Completed' });
        await loadAttendance(); // Refresh attendance data
        return { success: true };
      } else if (error) {
        return { success: false, error: error.message };
      }
    } catch (err) {
      return { success: false, error: 'Failed to update attendance' };
    }
  };

  const getAttendanceSummary = () => {
    if (attendance.length === 0) {
      return {
        total: 0,
        present: 0,
        absent: 0,
        percentage: 0
      };
    }

    const present = attendance.filter(record => record.status === 'Present').length;
    const total = attendance.length;
    const absent = total - present;
    const percentage = total > 0 ? Math.round((present / total) * 100) : 0;

    return {
      total,
      present,
      absent,
      percentage
    };
  };

  return {
    attendance,
    loading,
    error,
    updateAttendance,
    getAttendanceSummary,
    refresh: loadAttendance
  };
}
